import { Button } from '@neo/index'
import { cn } from '@neo/lib/utils'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { Badge } from '@neo/primitives/badge'
import { Bot, CircleCheck, CircleX, ExternalLink } from 'lucide-react'
import { useEffect } from 'react'
import { FlowNodeIconClass } from '../../../config/panel.config'
import { useFlowStore } from '../../../store'
import { AnimatedGradientText } from '../../vibe-agent/components/animated-gradient-text'
import type { EvalNode } from '../types'
import { EvalStatus } from '../types'
import { calculateNodeStatus } from '../utils'
import { TestCaseRow } from './test-case-row'

interface NodeSectionContext {
  currentlyTestingNodeUuid: string | null
  isPaused: boolean
  currentRunId: number | null
}

interface NodeSectionProps {
  node: EvalNode
  onToggle: () => void
  context: NodeSectionContext
}

const DEFAULT_SKIP_MESSAGE =
  'Node skipped - all test cases passed in previous simulation and node configuration unchanged.'

const SKIP_REASON_MESSAGES: Record<string, string> = {
  node_already_evaluated: DEFAULT_SKIP_MESSAGE,
  explicitly_skipped: 'Node skipped for this evaluation.',
}

const getSkipReasonMessage = (reason?: string) =>
  (reason && SKIP_REASON_MESSAGES[reason]) || DEFAULT_SKIP_MESSAGE

export const NodeSection = ({ node, onToggle, context }: NodeSectionProps) => {
  const selectNode = useFlowStore((state) => state.selectNode)
  const unselectNode = useFlowStore((state) => state.unselectNode)
  const selectedNodeID = useFlowStore((state) => state.selectedNodeID)
  const { currentlyTestingNodeUuid, isPaused, currentRunId } = context

  const isCancelled = isPaused || (currentRunId !== null && node.runId !== currentRunId)

  // Get icon from FlowNodeIconClass
  const nodeIcon = FlowNodeIconClass[node.nodeType]?.icon || (
    <Bot className="h-5 w-5 text-primary" />
  )

  // Calculate node status badge
  const getNodeStatusBadge = () => {
    // Skipped nodes
    if (node.status === EvalStatus.Skipped) {
      return {
        label: 'Skipped',
        color: 'text-muted-foreground',
        bg: 'bg-muted',
      }
    }

    if (node.status === EvalStatus.Testing) {
      return {
        label: isCancelled ? 'Simulation cancelled' : 'Simulating',
        color: isCancelled ? 'text-muted-foreground' : 'text-brand-text',
        bg: 'bg-transparent',
        animated: !isCancelled,
      }
    }

    if (node.status !== EvalStatus.Passed && node.status !== EvalStatus.Failed) return null

    const statusInfo = calculateNodeStatus(node.testCases)
    if (!statusInfo) return null

    // All passed
    if (statusInfo.status === EvalStatus.Passed) {
      return {
        icon: <CircleCheck className="h-3 w-3" aria-hidden />,
        label: 'Pass',
        color: 'text-success-tag-foreground',
        bg: 'bg-success-tag',
      }
    }

    // All failed
    if (statusInfo.status === EvalStatus.Failed) {
      return {
        icon: <CircleX className="h-3 w-3" aria-hidden />,
        label: 'Fail',
        color: 'text-status-danger',
        bg: 'bg-danger',
      }
    }

    // Partial pass
    return {
      icon: null,
      label: `${statusInfo.passedCount}/${statusInfo.totalCount} Pass`,
      color: 'text-status-warning',
      bg: 'bg-warning',
    }
  }

  const statusBadge = getNodeStatusBadge()

  // Auto-collapse when evaluation moves to next node
  useEffect(() => {
    const currentlyTesting = currentlyTestingNodeUuid
    const isThisNodeComplete =
      node.status === EvalStatus.Passed ||
      node.status === EvalStatus.Failed ||
      node.status === EvalStatus.Skipped
    const isDifferentNodeTesting = currentlyTesting && currentlyTesting !== node.nodeUuid

    if (isThisNodeComplete && isDifferentNodeTesting && node.isExpanded) {
      onToggle()
    }
  }, [currentlyTestingNodeUuid, node.status, node.isExpanded, node.nodeUuid, onToggle])

  const accordionItemValue = `${node.nodeUuid}-${node.runId}`
  const skippedMessage = getSkipReasonMessage(node.skipReason)

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full"
      value={node.isExpanded ? accordionItemValue : undefined}
      onValueChange={(value) => {
        if (value !== accordionItemValue && node.isExpanded) {
          onToggle()
        } else if (value === accordionItemValue && !node.isExpanded) {
          onToggle()
        }
      }}
    >
      <AccordionItem value={accordionItemValue} className="border-none">
        <AccordionTrigger
          iconPosition="left"
          className="w-full flex items-center gap-2 py-2 hover:bg-bg2 hover:no-underline transition-colors bg-background justify-start rounded-lg px-2"
          data-testid={`node-${node.nodeUuid}`}
        >
          <div className="flex items-center gap-2">
            <div
              className={`flex h-6 w-6 items-center justify-center rounded ${FlowNodeIconClass[node.nodeType]?.class || ''}`}
            >
              {nodeIcon}
            </div>
            <span className="text-p-400 text-secondary">{node.nodeName}</span>
          </div>
          <span>
            <Button
              asChild
              variant="ghost"
              size="xs"
              className="p-0 hover:bg-bg2 ml-1"
              onClick={(e) => {
                e.stopPropagation()
                if (selectedNodeID === node.nodeUuid) {
                  unselectNode()
                } else {
                  selectNode(node.nodeUuid)
                }
              }}
              data-testid={`select-node-${node.nodeUuid}`}
              tooltipContent="View node details"
            >
              <span>
                <ExternalLink className="size-3" />
              </span>
            </Button>
          </span>
          <span className="ml-auto">
            {statusBadge && (
              <Badge
                className={cn(
                  'text-xs-400 flex items-center gap-1 px-2 py-1',
                  statusBadge.bg,
                  statusBadge.color,
                  `hover:${statusBadge.bg}`,
                )}
              >
                {statusBadge.icon}
                {statusBadge.animated ? (
                  <AnimatedGradientText>{statusBadge.label}</AnimatedGradientText>
                ) : (
                  statusBadge.label
                )}
              </Badge>
            )}
          </span>
        </AccordionTrigger>
        <AccordionContent className="ml-4 pb-2 space-y-1 bg-background">
          {node.status === EvalStatus.Skipped && node.testCases.length === 0 ? (
            <div className="py-3 px-2 text-p-400 text-muted-foreground">{skippedMessage}</div>
          ) : (
            node.testCases.map((testCase) => (
              <TestCaseRow
                key={testCase.id}
                testCase={testCase}
                context={{ isPaused, currentRunId }}
              />
            ))
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
